import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigation } from '@react-navigation/native';

import { ContactSupport } from '@/components/ContactSupport';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useTrustlyKycStore } from '@/feature/kyc/hooks/use-trustly-kyc-store';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useRootNavigation } from '@/hooks/use-root-navigation';
import { useUserStore } from '@/hooks/use-user-store';
import { LightSdd, fetchAuthUserTrustlyInfo } from '@/hooks/use-user-trustly-information';
import { exitKycToLogin, navigateToKyc, popToHome, resetToAccountScreen } from '@/navigation/navigation';
import { IdpvSource, IdpvSourceType } from '@/navigation/types';

/**
 * Hook to navigate to error modal with context-specific content.
 * @param {Object} params - Hook parameters
 * @param {IdpvSourceType} [params.source=IdpvSource.KYC] - Source of the error
 * @returns Function to navigate to error modal
 */
export const useNavigateToErrorModal = ({ source }: { source?: IdpvSourceType } = {}) => {
    const navigation = useNavigation();
    const rootNavigation = useRootNavigation();
    const { t } = useTranslation(['kyc', 'common', 'manage_cards', 'account']);
    const actions = useUserStore(state => state.actions);
    const isTrustlyVerificationEnabled = useJurisdictionStore(
        state =>
            state.jurisdictionSettings?.globalSettings?.featureFlags?.trustly_withdrawal_verification_enabled
                ?.enabled ?? true
    );
    const navigateToErrorModal = useCallback(async () => {
        const handleCloseKyc = () => {
            BetrAnalytics.trackEvent(AnalyticsEvent.IDPV_FAILED_DISMISS);
            actions.logout();
            exitKycToLogin(navigation);
        };

        let title,
            subtitle,
            primaryButton,
            closeIconMode,
            handlePress,
            showSecondaryButton = true,
            secondaryButton,
            handleSecondaryButtonPress,
            closeIconHandlePress,
            showNavbarTitle = true,
            footer = null;

        switch (source) {
            case IdpvSource.ACCOUNT_SETTING:
                title = t('verification_account_settings_fail_title');
                subtitle = t('verification_account_settings_fail_description');
                primaryButton = t('common:done');
                showSecondaryButton = false;
                showNavbarTitle = false;
                closeIconMode = 'none' as const;
                handlePress = () => {
                    popToHome(rootNavigation, 'account');
                };
                break;
            case IdpvSource.MANAGE_CARD:
                title = t('manage_cards:not_able_to_complete_profile');
                subtitle = t('manage_cards:enhanced_profile_setup_failed');
                primaryButton = t('common:done');
                closeIconMode = 'close' as const;
                showSecondaryButton = false;
                closeIconHandlePress = () => resetToAccountScreen(rootNavigation, 'ManageDebitCards');
                handlePress = () => popToHome(rootNavigation, 'account');
                footer = <ContactSupport />;
                break;
            case IdpvSource.WITHDRAW: {
                const trustlyInfo = isTrustlyVerificationEnabled ? await fetchAuthUserTrustlyInfo() : undefined;
                const canCrossRedirectToTrustly =
                    isTrustlyVerificationEnabled && trustlyInfo?.LIGHT_SDD !== LightSdd.IGNORE;

                title = t('account:withdraw_verification_failed_title');
                subtitle = t('account:withdraw_verification_failed_description');
                primaryButton = t('common:try_again');
                closeIconMode = 'close' as const;
                handlePress = () => {
                    BetrAnalytics.trackProductEvent(AnalyticsEvent.WITHDRAW_VERIFICATION_IDPV_RETRY);
                    navigateToKyc(navigation, { screen: 'KycScanId', params: { source: IdpvSource.WITHDRAW } });
                };
                showSecondaryButton = canCrossRedirectToTrustly;
                if (canCrossRedirectToTrustly) {
                    secondaryButton = t('account:trustly_withdraw_option_title');
                    handleSecondaryButtonPress = () => {
                        BetrAnalytics.trackProductEvent(AnalyticsEvent.WITHDRAW_VERIFICATION_SELECTED_TRUSTLY, {
                            source: 'idpv_failure',
                        });
                        useTrustlyKycStore.getState().setShouldProceedToWithdrawal(true);
                        navigateToKyc(navigation, { screen: 'TrustlyId' });
                    };
                }
                closeIconHandlePress = () => popToHome(rootNavigation, 'account');
                footer = <ContactSupport />;
                break;
            }
            case IdpvSource.KYC:
                title = t('verification_failed_header');
                subtitle = t('verification_failed_copy_account_locked');
                primaryButton = t('common:done');
                closeIconMode = 'close' as const;
                handlePress = handleCloseKyc;
                closeIconHandlePress = handleCloseKyc;
                showSecondaryButton = false;
                footer = <ContactSupport />;
                break;
            default: // If IdpvSource is undefined, null, empty
                title = t('attempt_incomplete_title');
                subtitle = t('attempt_incomplete_subtitle');
                primaryButton = t('common:done');
                closeIconMode = 'close' as const;
                handlePress = handleCloseKyc;
                closeIconHandlePress = handleCloseKyc;
                showSecondaryButton = false;
                footer = <ContactSupport />;
                break;
        }

        navigation.navigate('ErrorModal', {
            title,
            subtitle,
            primaryButton,
            closeIconMode,
            showSecondaryButton,
            secondaryButton,
            handleSecondaryButtonPress,
            closeIconHandlePress,
            handlePress,
            showNavbarTitle,
            footer,
        });
    }, [source, t, navigation, rootNavigation, actions, isTrustlyVerificationEnabled]);

    return navigateToErrorModal;
};
